import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { BadgeProvider, useBadges } from '../../hooks/useBadges';
import { Users, Calendar, FileText, TrendingUp, Megaphone, Mail, Archive, Briefcase, UserPlus, ScrollText, ClipboardList, AlertTriangle } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import PanelHeader from '../../components/PanelHeader';
import EmployeeList from './EmployeeList';
import Communications from './Communications';
import Documents from './Documents';
import Announcements from './Announcements';
import HRCommunication from './HRCommunication';
import WorkCalendar from '../work/WorkCalendar';
import HRArchive from './HRArchive';
import HRJobs from './HRJobs';
import TalentPool from './TalentPool';
import HRRules from './HRRules';
import HRPersonnelRequests from './HRPersonnelRequests';
import HRWarnings from './HRWarnings';

const HRPanelContent = () => {
  const location = useLocation();
  const { getBadgeCount, markSeen } = useBadges();
  const [unreadCounts, setUnreadCounts] = useState({});
  const [communicationsTotalUnread, setCommunicationsTotalUnread] = useState(0);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [newFilesCount, setNewFilesCount] = useState(0);
  const [unseenPersonnelCount, setUnseenPersonnelCount] = useState(0);

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Genel bildirim sayılarını al
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCounts(data);
      }
      
      // İK İletişim toplam okunmamış sayısını al
      const commResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/communications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (commResponse.ok) {
        const commData = await commResponse.json();
        setCommunicationsTotalUnread(commData.total || 0);
      }
    } catch (error) {
      console.error('Okunmamış sayıları alınamadı:', error);
    }
  }, []);

  // Dosyalar yeni belge sayısını çek
  const fetchNewFilesCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/archive/new-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNewFilesCount(data.new_count || 0);
      }
    } catch (error) {
      console.error('Dosyalar yeni belge sayısı alınamadı:', error);
    }
  }, []);

  // Personel Talepleri okunmamış (yeni) talep sayısını çek
  const fetchUnseenPersonnelCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/personnel-requests/unseen-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnseenPersonnelCount(data.unseen_count || 0);
      }
    } catch (error) {
      console.error('Okunmamış personel talebi sayısı alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    fetchNewFilesCount();
    fetchUnseenPersonnelCount();
    const interval = setInterval(() => {
      fetchUnreadCounts();
      fetchNewFilesCount();
      fetchUnseenPersonnelCount();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts, fetchNewFilesCount, fetchUnseenPersonnelCount]);

  useEffect(() => {
    fetchUnreadCounts();
    fetchUnseenPersonnelCount();
  }, [location.pathname, fetchUnreadCounts, fetchUnseenPersonnelCount]);

  const menuItems = [
    { path: '/hr/employees', icon: Users, label: 'Çalışanlar', menuKey: 'employees', badgeKey: null },
    { path: '/hr/calendar', icon: Calendar, label: 'Takvim', menuKey: 'calendar', badgeKey: null },
    { path: '/hr/jobs', icon: Briefcase, label: 'İşler', menuKey: 'jobs', badgeKey: null },
    { path: '/hr/iletisim', icon: Mail, label: 'İletişim', menuKey: 'communication', badgeKey: 'messages' },
    { path: '/hr/announcements', icon: Megaphone, label: 'Duyurular', menuKey: 'announcements', badgeKey: null },
    { path: '/hr/documents', icon: FileText, label: 'Belgeler', menuKey: 'documents', badgeKey: null },
    { path: '/hr/performance', icon: TrendingUp, label: 'Performans', menuKey: 'performance', disabled: true, badgeKey: null },
    { path: '/hr/archive', icon: Archive, label: 'Dosyalar', menuKey: 'archive', badgeKey: null, badge: newFilesCount > 0 ? newFilesCount : null, isNewBadge: newFilesCount > 0 },
    { path: '/hr/talent-pool', icon: UserPlus, label: 'Talent Pool', menuKey: 'talentpool', badgeKey: null },
    { path: '/hr/rules', icon: ScrollText, label: 'Kurallar', menuKey: 'rules', badgeKey: null },
    { path: '/hr/personnel-requests', icon: ClipboardList, label: 'Personel Talepleri', menuKey: 'personnel_requests', badgeKey: null, badge: unseenPersonnelCount > 0 ? unseenPersonnelCount : null, isNewBadge: unseenPersonnelCount > 0 },
    { path: '/hr/warnings', icon: AlertTriangle, label: 'Uyarılar', menuKey: 'warnings', badgeKey: null },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="hr-panel">
      {/* Header */}
      <PanelHeader
        logoSrc="https://customer-assets.emergentagent.com/job_independent-erp/artifacts/prgex85y_18_01_2025_fortis_kurumsal_logolar.svg"
      >
        <h1 className="text-2xl font-bold uppercase tracking-wide">İK PANELİ</h1>
      </PanelHeader>

      {/* Add pt-16 to account for fixed header height (h-16 = 4rem) */}
      <div className="flex overflow-hidden pt-16">
        {/* Collapsible Sidebar */}
        <aside 
          className={`fixed top-16 left-0 h-[calc(100vh-4rem)] border-r border-[hsl(var(--border))] bg-white transition-all duration-300 z-40 ${
            sidebarExpanded ? 'w-64' : 'w-14'
          }`}
          onMouseEnter={() => setSidebarExpanded(true)}
          onMouseLeave={() => setSidebarExpanded(false)}
        >
          <nav className={`space-y-1 ${sidebarExpanded ? 'p-4' : 'p-2'}`}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              // Badge sayısını belirle - önce yeni badge sisteminden
              let badgeCount = getBadgeCount(item.menuKey);
              if (badgeCount === 0) {
                if (item.badgeKey === 'communications_total' && communicationsTotalUnread > 0) {
                  badgeCount = communicationsTotalUnread;
                } else if (item.badgeKey && unreadCounts[item.badgeKey] > 0) {
                  badgeCount = unreadCounts[item.badgeKey];
                } else if (item.badge) {
                  // Custom badge (örn: Dosyalar için yeni belge sayısı)
                  badgeCount = item.badge;
                }
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.disabled ? '#' : item.path}
                  onClick={(e) => {
                    if (item.disabled) e.preventDefault();
                    else if (item.menuKey) markSeen(item.menuKey);
                  }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                    item.disabled
                      ? 'opacity-50 cursor-not-allowed bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'
                      : isActive(item.path)
                      ? 'bg-[hsl(var(--brand-700))] text-white'
                      : 'hover:bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]'
                  } ${!sidebarExpanded ? 'justify-center' : ''}`}
                  data-testid={`hr-menu-${item.label.toLowerCase()}`}
                  title={!sidebarExpanded ? item.label : undefined}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  {sidebarExpanded && (
                    <>
                      <span className="font-medium">{item.label}</span>
                      {item.disabled && <span className="ml-auto text-xs">(Yakında)</span>}
                      {!item.disabled && badgeCount > 0 && (
                        <Badge className={`ml-auto ${
                          isActive(item.path) ? 'bg-white text-[hsl(var(--brand-700))]' : 'bg-red-500 text-white animate-pulse'
                        }`}>
                          {badgeCount}
                        </Badge>
                      )}
                    </>
                  )}
                  {!sidebarExpanded && !item.disabled && badgeCount > 0 && (
                    <Badge className="absolute top-0 right-0 h-4 w-4 p-0 text-[10px] bg-red-500 text-white">
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 p-6 overflow-x-auto min-w-0 transition-all duration-300 ${sidebarExpanded ? 'ml-64' : 'ml-14'}`}>
          <Routes>
            <Route path="/employees" element={<EmployeeList />} />
            <Route path="/calendar" element={<WorkCalendar enableHolidays={true} />} />
            <Route path="/jobs" element={<HRJobs />} />
            <Route path="/personnel-requests" element={<HRPersonnelRequests />} />
            <Route path="/warnings" element={<HRWarnings />} />
            <Route path="/communications" element={<Communications />} />
            <Route path="/iletisim" element={<HRCommunication />} />
            <Route path="/announcements" element={<Announcements />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/archive" element={<HRArchive />} />
            <Route path="/talent-pool" element={<TalentPool />} />
            <Route path="/rules" element={<HRRules />} />
            {/* Future routes: /performance */}
          </Routes>
        </main>
      </div>
    </div>
  );
};

export const HRPanel = () => {
  return (
    <BadgeProvider panel="hr">
      <HRPanelContent />
    </BadgeProvider>
  );
};

export default HRPanel;
